import React, { useEffect, useState } from "react";
import { Modal, Form, InputNumber, Button, Space, message, notification, Descriptions, Select, Input, Alert, Row, Col, Spin, Radio, Typography } from "antd";
import { InfoCircleOutlined, ArrowRightOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { contractManagementService } from "../../../../api/enhancedApi";
import { settlementApi } from "../../../../api/settlementApi";
import { useCurrencies } from "../../../../hooks/useCurrencies";

const { TextArea } = Input;
const { Text } = Typography;

const LegCostModal = ({ visible, legData, prefilledCostType, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currency, setCurrency] = useState("USD");
    const [exchangeRate, setExchangeRate] = useState(1.0);
    const [amount, setAmount] = useState(0);
    const [usdAmount, setUsdAmount] = useState(0);
    // 汇率自动带出状态：loading=查询中, notFound=该日期查不到汇率(需手填), autoDate=本次带出所用的费用日期
    const [rateLoading, setRateLoading] = useState(false);
    const [rateNotFound, setRateNotFound] = useState(false);
    const [rateAutoDate, setRateAutoDate] = useState(null);
    // 自动带出汇率所用的汇率表记录主键（用于落账记下用了哪一条；用户手改汇率后仍保留，供后端记录表内建议值）
    const [rateRecordId, setRateRecordId] = useState(null);
    const [notifyApi, contextHolder] = notification.useNotification();
    const navigate = useNavigate();

    // 供应商数据状态
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);

    // 二次确认（仅当预检发现重复时才弹）：与 Excel 导入同一交互范式
    const [precheckLoading, setPrecheckLoading] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);          // precheck 返回的重复明细
    const [confirmVisible, setConfirmVisible] = useState(false);       // 二次确认弹窗开关
    const [pendingSubmitData, setPendingSubmitData] = useState(null);  // 暂存待提交数据
    const [importMode, setImportMode] = useState("overwrite");         // 用户在弹窗里选的处理方式

    // 费用类型选项（与公共费用页面保持一致）
    const costTypeOptions = [
        { value: 'bh', label: 'bh-BH时间' },
        { value: 'fuel', label: 'fuel-燃油费' },
        { value: 'landing_parking', label: 'landing_parking-起降停场费' },
        { value: 'ground_handling', label: 'ground_handling-地面服务费' },
        { value: 'catering', label: 'catering-配餐费' },
        { value: 'transportation', label: 'transportation-交通费' },
        { value: 'deicing', label: 'deicing-除冰费' },
        { value: 'navigation', label: 'navigation-导航费' },
        { value: 'positioning', label: 'positioning-调机费' },
        { value: 'laundry', label: 'laundry-洗涤费' },
        { value: 'accommodation', label: 'accommodation-住宿费' },
        { value: 'disinfection', label: 'disinfection-消毒费' },
        { value: 'flight_service_charge', label: 'flight_service_charge-飞行服务费' },
        { value: 'uld', label: 'uld-ULD租赁费' },
        { value: 'traffic_rights_permit', label: 'traffic_rights_permit-航权及许可费' },
        { value: 'sita', label: 'sita-SITA费' },
        { value: 'commission', label: 'commission-佣金费' },
        { value: 'acmi', label: 'acmi-ACMI费用' },
        { value: 'cargo_handling', label: 'cargo_handling-货物操作费' },
        { value: 'traffic_rights', label: 'traffic_rights-航权费' },
        { value: 'trucking_fee', label: 'trucking_fee-卡车运输费' },
        { value: 'others_fee', label: 'others_fee-其他费用' }
    ];

    // 币种选项：统一从后端取(单一来源 COMMON_CURRENCIES),不再前端硬编码
    const { currencyOptions } = useCurrencies();

    // 获取供应商列表。走可选供应商口径：暂停的供应商由服务端过滤，不再按启用筛
    const loadSuppliers = async () => {
        try {
            setSuppliersLoading(true);

            const response = await contractManagementService.businessPartner.getSuppliers({
                page: 1,
                page_size: 500,
            });

            if (response && response.data && response.data.items) {
                const suppliers = response.data.items.map(supplier => ({
                    value: supplier.id,
                    label: supplier.name || supplier.code, // 优先显示name，没有则显示code
                    name: supplier.name,
                    code: supplier.code,
                    ...supplier
                }));
                setSupplierOptions(suppliers);

            } else {
                console.warn("供应商API返回数据格式异常:", response);
                message.warning("获取供应商数据格式异常");
                setSupplierOptions([]);
            }

        } catch (error) {
            console.error("获取供应商列表失败:", error);
            message.error("获取供应商列表失败: " + error.message);
            setSupplierOptions([]);
        } finally {
            setSuppliersLoading(false);
        }
    };

    // 当模态框打开时，重置表单并加载供应商
    // T26a — 若外部传入 prefilledCostType,优先用它预填(从 S3"去录入"或下钻抽屉"补录"跳过来)
    useEffect(() => {
        if (visible && legData) {
            form.setFieldsValue({
                cost_type: prefilledCostType || undefined,
                supplier: undefined,
                currency: "USD",
                amount: 0,
                exchange_rate: 1.0,
                remarks: "",
            });
            setCurrency("USD");
            setExchangeRate(1.0);
            setAmount(0);
            setUsdAmount(0);
            setRateLoading(false);
            setRateNotFound(false);
            setRateAutoDate(null);
            setRateRecordId(null);
            setConfirmVisible(false);
            setDuplicateInfo(null);
            setPendingSubmitData(null);
            setImportMode("overwrite");
            
            // 加载供应商列表
            loadSuppliers();
        }
    }, [visible, legData, prefilledCostType, form]);

    // 监听金额和汇率变化，自动计算USD金额
    useEffect(() => {
        const calculatedUsdAmount = amount * exchangeRate;
        setUsdAmount(calculatedUsdAmount);
    }, [amount, exchangeRate]);

    // 取费用日期作为汇率查询日期（费用日期=航班所处日期）；"未知"/空则不带日期
    const getRateQueryDate = () => {
        const raw = legData?.operation_date;
        return raw && raw !== "未知" ? raw : undefined;
    };

    // 按币种 + 费用日期自动带出汇率
    const fetchRateForCurrency = async (cur) => {
        if (!cur || cur === "USD") return;

        const targetDate = getRateQueryDate();
        setRateLoading(true);
        setRateNotFound(false);
        try {
            const resp = await settlementApi.exchangeRate.getCurrentRate(cur, "USD", targetDate);
            const data = resp?.data || {};

            if (data.found && data.rate != null) {
                const r = Number(data.rate);
                setExchangeRate(r);
                form.setFieldsValue({ exchange_rate: r });
                setRateNotFound(false);
                setRateAutoDate(targetDate || null);
                setRateRecordId(data.exchange_rate_id || null);
            } else {
                // 查不到对应日期汇率：清空让用户手填，不使用任何假汇率
                setExchangeRate(null);
                form.setFieldsValue({ exchange_rate: undefined });
                setRateNotFound(true);
                setRateAutoDate(null);
                setRateRecordId(null);
                message.warning(`未找到 ${cur}→USD 在 ${targetDate || "当前日期"} 的汇率，请手动填写`);
            }
        } catch (error) {
            console.error("自动获取汇率失败:", error);
            setExchangeRate(null);
            form.setFieldsValue({ exchange_rate: undefined });
            setRateNotFound(true);
            setRateAutoDate(null);
            setRateRecordId(null);
            message.warning("汇率查询失败，请手动填写汇率");
        } finally {
            setRateLoading(false);
        }
    };

    // 处理币种变化
    const handleCurrencyChange = (value) => {
        setCurrency(value);
        if (value === "USD") {
            setExchangeRate(1.0);
            form.setFieldsValue({ exchange_rate: 1.0 });
            setRateLoading(false);
            setRateNotFound(false);
            setRateAutoDate(null);
            setRateRecordId(null);
        } else {
            fetchRateForCurrency(value);
        }
    };

    // 处理金额变化
    const handleAmountChange = (value) => {
        setAmount(value || 0);
        form.setFieldsValue({ amount: value });
    };

    // 处理汇率变化（手动覆盖）
    const handleExchangeRateChange = (value) => {
        setExchangeRate(value ?? null);
        form.setFieldsValue({ exchange_rate: value });
        // 用户开始手填，清除"未找到"提示与"自动带出"标记
        setRateNotFound(false);
        setRateAutoDate(null);
    };

    // 处理重置
    const handleReset = () => {
        form.resetFields();
        setCurrency("USD");
        setExchangeRate(1.0);
        setAmount(0);
        setUsdAmount(0);
        setRateLoading(false);
        setRateNotFound(false);
        setRateAutoDate(null);
        setRateRecordId(null);
        setConfirmVisible(false);
        setDuplicateInfo(null);
        setPendingSubmitData(null);
        setImportMode("overwrite");
    };

    // 真正执行录入(处理方式已确定后调用): mode = overwrite | accumulate
    const doSubmit = async (submitData, mode) => {
        try {
            setLoading(true);
            const response = await settlementApi.legCost.createManualLegCost({
                ...submitData,
                import_mode: mode,
            });

            if (response.data.success) {
                const { is_new, change_detail, affected_batch_ids } = response.data.data;

                if (is_new) {
                    message.success("费用录入成功");
                } else if (mode === "accumulate") {
                    const added = change_detail?.added_amount || 0;
                    message.success(`费用叠加成功（+${added.toFixed(2)} USD）`);
                } else {
                    const amountChange = change_detail?.amount_change || 0;
                    message.success(`费用更新成功（金额变更: ${amountChange > 0 ? '+' : ''}${amountChange.toFixed(2)} USD）`);
                }

                if (affected_batch_ids && affected_batch_ids.length > 0) {
                    notifyApi.info({
                        message: "批次需要重新计算",
                        description: `已标记 ${affected_batch_ids.length} 个批次需要重新计算`,
                        duration: 8,
                        btn: (
                            <Space>
                                <Button size="small" onClick={() => notifyApi.destroy()}>
                                    稍后处理
                                </Button>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<ArrowRightOutlined />}
                                    onClick={() => {
                                        notifyApi.destroy();
                                        navigate("/settlement/batch-calculations");
                                    }}
                                >
                                    前往刷新
                                </Button>
                            </Space>
                        ),
                    });
                }

                onSuccess();
            } else {
                throw new Error(response.data.message || "录入失败");
            }
        } catch (error) {
            console.error("费用录入失败:", error);

            let errorMsg = "录入失败";
            const detail = error.response?.data?.detail;
            if (detail) {
                if (typeof detail === "string") {
                    errorMsg = detail;
                } else if (Array.isArray(detail)) {
                    errorMsg = detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
                } else if (typeof detail === "object") {
                    errorMsg = detail.msg || JSON.stringify(detail);
                }
            } else if (error.message) {
                errorMsg = error.message;
            }

            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 点击"确认录入"：先预检；无重复直接录入，有重复才弹二次确认让用户选覆盖/叠加(与 Excel 导入一致)
    const handleSubmit = async () => {
        let submitData;
        try {
            const values = await form.validateFields();

            // USD金额在提交时按校验后的 values 现算，避免依赖异步派生的 usdAmount state 产生脏值
            const originalAmount = Number(values.amount || 0);
            const submitRate = values.currency === "USD" ? 1.0 : Number(values.exchange_rate || 0);
            const computedUsdAmount = Number((originalAmount * submitRate).toFixed(2));

            submitData = {
                flight_operation_id: legData?.id,        // 从列表行获取
                cost_type: values.cost_type,
                supplier_id: values.supplier,
                original_amount: originalAmount,
                original_currency: values.currency,
                exchange_rate: submitRate,
                base_total_amount: computedUsdAmount,    // 计算得到的USD金额
                flight_date: getRateQueryDate() || undefined,  // 费用发生的航班日（服务端缺省时反查航班）
                exchange_rate_id: rateRecordId || undefined,   // 用了汇率表哪一条（记入附加信息）
                remarks: values.remarks || '',
            };
        } catch (validationError) {
            // 表单校验失败,antd 已在字段上提示,直接返回
            return;
        }

        // 预检：检测同航段 + 费用类型 + 供应商是否已存在
        setPrecheckLoading(true);
        try {
            const res = await settlementApi.legCost.precheckManualLegCost({
                flight_operation_id: submitData.flight_operation_id,
                cost_type: submitData.cost_type,
                supplier_id: submitData.supplier_id,
                original_amount: submitData.original_amount,
                original_currency: submitData.original_currency,
                exchange_rate: submitData.exchange_rate,
            });
            const precheckData = res.data?.data || {};

            if (precheckData.match_count > 0) {
                // 有重复 → 暂存数据，弹二次确认让用户选覆盖/叠加
                setPendingSubmitData(submitData);
                setDuplicateInfo(precheckData);
                setImportMode("overwrite");
                setConfirmVisible(true);
            } else {
                // 无重复 → 直接录入（新增）
                await doSubmit(submitData, "overwrite");
            }
        } catch (error) {
            console.error("预检失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "预检失败";
            message.error("预检失败：" + errorMsg);
        } finally {
            setPrecheckLoading(false);
        }
    };

    // 二次确认弹窗里点"执行"：按用户选择的 importMode 录入
    const handleConfirmDuplicate = async () => {
        setConfirmVisible(false);
        if (pendingSubmitData) {
            await doSubmit(pendingSubmitData, importMode);
        }
    };

    // 关闭模态框时重置表单
    const handleCancel = () => {
        handleReset();
        onCancel();
    };

    return (
        <>
        {contextHolder}
        <Modal
            title="成本录入"
            open={visible}
            onCancel={handleCancel}
            width={800}
            destroyOnHidden
            maskClosable={false}
            styles={{
                body: {
                    padding: '20px 24px',
                    maxHeight: '70vh',
                    overflowY: 'auto'
                }
            }}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="reset" onClick={handleReset}>
                    重置
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading || precheckLoading}
                >
                    确认录入
                </Button>
            ]}
        >
            <div style={{ minHeight: '400px' }}>
                {legData && (
                    <>
                        {/* 基本信息 */}
                        <div style={{ marginBottom: 16 }}>
                            <h3 style={{ marginBottom: 12, fontSize: 14, fontWeight: 500 }}>基本信息</h3>
                            <Descriptions
                                bordered
                                size="small"
                                column={3}
                                style={{ backgroundColor: "#fafafa" }}
                            >
                                <Descriptions.Item label="航段">
                                    {legData.leg || "第1段 ()"}
                                </Descriptions.Item>
                                <Descriptions.Item label="航班号">
                                    {legData.flight_number}
                                </Descriptions.Item>
                                <Descriptions.Item label="费用日期">
                                    {legData.operation_date}
                                </Descriptions.Item>
                            </Descriptions>
                        </div>

                        {/* 费用录入表单 */}
                        <div>
                            <h3 style={{ marginBottom: 12, fontSize: 14, fontWeight: 500 }}>费用录入</h3>
                            <Form form={form} layout="vertical" preserve={false}>
                                {/* 费用类型和供应商放在一行 */}
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="cost_type"
                                            label="费用类型"
                                            rules={[{ required: true, message: "请选择费用类型" }]}
                                        >
                                            <Select
                                                placeholder="请选择费用类型"
                                                options={costTypeOptions}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="supplier"
                                            label="供应商"
                                            rules={[{ required: true, message: "请选择供应商" }]}
                                        >
                                            <Select
                                                placeholder={suppliersLoading ? "加载中..." : "请选择供应商"}
                                                options={supplierOptions}
                                                loading={suppliersLoading}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                                                    option?.name?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                notFoundContent={suppliersLoading ? <Spin size="small" /> : "暂无供应商数据"}
                                                disabled={suppliersLoading}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* 币种和金额放在一行 */}
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="currency"
                                            label="币种"
                                            initialValue="USD"
                                            rules={[{ required: true, message: "请选择币种" }]}
                                        >
                                            <Select
                                                options={currencyOptions}
                                                onChange={handleCurrencyChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="amount"
                                            label="金额"
                                            rules={[
                                                { required: true, message: "请输入金额" },
                                                {
                                                    validator: (_, value) => {
                                                        if (value === null || value === undefined) return Promise.resolve();
                                                        if (value === 0) return Promise.reject(new Error("金额不能为 0"));
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: "100%" }}
                                                placeholder="正数为费用，负数为退费/调整"
                                                precision={2}
                                                addonAfter={currency}
                                                onChange={handleAmountChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* 汇率单独一行 */}
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="exchange_rate"
                                            label={
                                                <Space size={6}>
                                                    <span>汇率</span>
                                                    {rateLoading && <Spin size="small" />}
                                                </Space>
                                            }
                                            initialValue={1.0}
                                            extra={
                                                currency !== "USD" && !rateLoading && rateAutoDate
                                                    ? `已按费用日期 ${rateAutoDate} 自动带出，可手动修改`
                                                    : undefined
                                            }
                                            rules={[
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        const cur = getFieldValue("currency");
                                                        if (cur && cur !== "USD" && (value == null || value <= 0)) {
                                                            return Promise.reject(new Error("请填写汇率（未自动带出时需手动输入）"));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                }),
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: "100%" }}
                                                placeholder={rateLoading ? "查询中..." : "1.000000"}
                                                precision={6}
                                                min={0}
                                                addonAfter="→USD"
                                                onChange={handleExchangeRateChange}
                                                disabled={currency === "USD" || rateLoading}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* 汇率未找到提示 */}
                                {currency !== "USD" && rateNotFound && (
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Alert
                                                message={`未找到 ${currency}→USD 在 ${getRateQueryDate() || "当前日期"} 的汇率，请手动填写汇率`}
                                                type="warning"
                                                showIcon
                                                style={{ marginBottom: 16 }}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                {/* USD汇率提示 */}
                                {currency === "USD" && (
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Alert
                                                message={
                                                    <span>
                                                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                                                        USD汇率固定为 1.00
                                                    </span>
                                                }
                                                type="info"
                                                showIcon={false}
                                                style={{ marginBottom: 16 }}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                {/* USD金额（只读） */}
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item label="USD金额">
                                            <InputNumber
                                                style={{ width: "100%" }}
                                                value={usdAmount}
                                                precision={2}
                                                addonAfter="USD"
                                                disabled
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* 备注 */}
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="remarks"
                                            label="备注"
                                        >
                                            <TextArea
                                                placeholder="请输入备注信息（可选）"
                                                maxLength={200}
                                                showCount
                                                rows={4}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </>
                )}
            </div>
        </Modal>

        {/* 二次确认弹窗：仅当预检发现重复时弹出 —— 与 Excel 导入同一范式 */}
        <Modal
            title={
                <span style={{ color: '#cf1322' }}>
                    <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                    检测到重复 — 请确认处理方式
                </span>
            }
            open={confirmVisible}
            onCancel={() => setConfirmVisible(false)}
            onOk={handleConfirmDuplicate}
            okText={importMode === 'overwrite' ? '执行覆盖' : '执行叠加'}
            okButtonProps={{ danger: true }}
            cancelText="再看看"
            confirmLoading={loading}
            width={620}
            maskClosable={false}
            destroyOnHidden
        >
            {duplicateInfo?.match_details?.[0] && (() => {
                const d = duplicateInfo.match_details[0];
                return (
                    <div>
                        <Alert
                            type="error"
                            showIcon
                            message="该航段已存在相同费用类型 + 供应商的记录"
                            description="叠加可以保留多笔；如果选择覆盖，系统会把这一组的全部现有费用合并替换为本次的一笔金额。"
                            style={{ marginBottom: 16 }}
                        />
                        <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="费用类型">{d.cost_type}</Descriptions.Item>
                            <Descriptions.Item label="供应商">{d.supplier_name || '—'}</Descriptions.Item>
                            <Descriptions.Item label="库内现有">
                                {Number(d.existing_line_item_count || 1)} 笔
                            </Descriptions.Item>
                            <Descriptions.Item label="库内合计">
                                {Number(d.existing_original_amount || 0).toFixed(2)} {d.currency}
                            </Descriptions.Item>
                            <Descriptions.Item label="本次金额">
                                {Number(d.new_original_amount || 0).toFixed(2)} {d.currency}
                            </Descriptions.Item>
                        </Descriptions>
                        <Radio.Group
                            value={importMode}
                            onChange={(e) => setImportMode(e.target.value)}
                            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                        >
                            <Radio value="overwrite">
                                <Text strong style={{ color: '#cf1322' }}>覆盖</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    用本次金额替换这一组的全部现有费用，并合并为一笔（旧记录可追溯）
                                </Text>
                            </Radio>
                            <Radio value="accumulate">
                                <Text strong>叠加</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    新增一笔独立费用，现有费用保持不变（适用于分多笔结算）
                                </Text>
                            </Radio>
                        </Radio.Group>
                    </div>
                );
            })()}
        </Modal>
        </>
    );
};

export default LegCostModal;
